import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * Backend endpoint for the AI chat.
 *
 * Currently pointed at the local dev server (`npm run dev:local` in
 * sunday-chat-api/ — see its README) since there's no deployment yet.
 * Swap this back to the real backend URL once one is deployed.
 */
const CHAT_API_URL = 'http://localhost:3000/api/chat';

const SESSION_STORAGE_KEY = 'sunday:ai-chat:history';
const OPEN_STORAGE_KEY = 'sunday:ai-chat:open';
const MAX_STORED_MESSAGES = 20;

/**
 * @typedef {{ role: 'user' | 'assistant', content: string, products?: ProductResult[] }} ChatMessage
 * @typedef {{ title: string, handle: string, description: string|null, url: string|null, imageUrl: string|null, price: string|null, currency: string|null, available: boolean }} ProductResult
 * @typedef {{ title: string, handle: string, description: string, url: string, price: string, currency: string, available: boolean }} PageProductContext
 */

/**
 * Reads the current-page product context injected by ai-chat-widget.liquid
 * (present only on product pages), so the assistant can answer questions
 * about "this product" without the visitor needing to name it or the model
 * needing to call search_products.
 *
 * @returns {PageProductContext | null}
 */
function readPageContext() {
  const el = document.getElementById('ai-chat-page-context');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/**
 * The `<ai-chat>` conversational widget: a small floating panel (Messenger
 * style) toggled by the `ai-chat-fab` button that's a sibling of this
 * element in `ai-chat-widget.liquid` — not a full-height `<theme-drawer>`.
 * Owns its own open/close state via the `open` attribute (styled by
 * `.ai-chat[open]` in the snippet's stylesheet), message list, composer
 * form, and the fetch() call to the sunday-chat-api backend.
 *
 * Dispatches {@link AiChatErrorEvent} when a request to the backend fails,
 * so the host page can react (e.g. surface a toast) if desired.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} messages - Scrollable list where message bubbles are appended.
 * @property {HTMLFormElement} form - The message-composer form.
 * @property {HTMLTextAreaElement} input - The message text input.
 * @property {HTMLButtonElement} submit - The send button.
 *
 * @extends {Component<Refs>}
 */
export class AiChat extends Component {
  requiredRefs = ['messages', 'form', 'input', 'submit'];

  /** @type {ChatMessage[]} */
  #history = [];

  /** @type {boolean} */
  #isSending = false;

  /**
   * @returns {boolean}
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#history = this.#loadHistory();
    this.#renderHistory();

    // Restore the open/closed state across page navigations (e.g. the user
    // clicked a product card) so the conversation stays visibly continuous
    // instead of silently closing on every new page.
    if (this.#loadOpenState()) {
      this.open({ skipPersist: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeOutsideListeners();
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.skipPersist] - True when restoring state on page load — avoids redundantly rewriting the same value and stealing focus/scroll on arrival.
   */
  open({ skipPersist = false } = {}) {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    this.#getFab()?.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.#onKeyDown);
    // Deferred so the click that opened the panel doesn't immediately
    // register as an "outside" click and close it again.
    queueMicrotask(() => document.addEventListener('click', this.#onDocumentClick));
    if (!skipPersist) {
      this.refs.input.focus();
      this.#saveOpenState(true);
    }
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.#getFab()?.setAttribute('aria-expanded', 'false');
    this.#removeOutsideListeners();
    this.#saveOpenState(false);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  #removeOutsideListeners() {
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getFab() {
    return this.closest('.ai-chat-widget')?.querySelector('.ai-chat-fab') ?? null;
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      this.close();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const widget = this.closest('.ai-chat-widget');
    if (widget && isClickedOutside(event, widget)) {
      this.close();
    }
  };

  /**
   * Clears the conversation and starts fresh. Bound via
   * `on:click="#ai-chat-panel/resetConversation"` on the header's reset
   * button.
   */
  resetConversation() {
    if (this.#isSending) return;
    this.#history = [];
    this.#saveHistory();
    this.#renderHistory();
    this.refs.input.value = '';
    this.refs.input.focus();
  }

  /**
   * Handles the composer form submit. Bound via `on:submit="/handleSubmit"`
   * in the markup (closest-Component resolution — see component.js).
   *
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();
    if (this.#isSending) return;

    const { input } = this.refs;
    const text = input.value.trim();
    if (!text) return;

    input.value = '';
    this.#sendMessage(text);
  }

  /**
   * @param {string} text
   */
  async #sendMessage(text) {
    this.#appendMessage({ role: 'user', content: text });
    this.#setSending(true);

    const thinkingEl = this.#appendThinkingIndicator();

    try {
      const response = await fetch(CHAT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: this.#history.map(({ role, content }) => ({ role, content })),
          pageContext: readPageContext(),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `Request failed (${response.status})`);
      }

      const data = await response.json();
      thinkingEl.remove();
      this.#appendMessage({
        role: 'assistant',
        content: data.message || '',
        products: Array.isArray(data.products) ? data.products : undefined,
      });
    } catch (error) {
      console.error('[ai-chat] request failed:', error);
      thinkingEl.remove();
      this.#appendMessage({
        role: 'assistant',
        content: "Sorry, I can't respond right now. Please try again in a moment.",
      });
      this.dispatchEvent(new AiChatErrorEvent(/** @type {Error} */ (error)));
    } finally {
      this.#setSending(false);
    }
  }

  /**
   * @param {boolean} sending
   */
  #setSending(sending) {
    this.#isSending = sending;
    this.refs.submit.disabled = sending;
    this.refs.input.disabled = sending;
  }

  /**
   * @returns {HTMLElement} The thinking-indicator element, so the caller can remove it once the reply arrives.
   */
  #appendThinkingIndicator() {
    const el = document.createElement('div');
    el.className = 'ai-chat__message ai-chat__message--assistant ai-chat__message--thinking';
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = '<span class="ai-chat__dot"></span><span class="ai-chat__dot"></span><span class="ai-chat__dot"></span>';
    this.refs.messages.append(el);
    this.#scrollToBottom();
    return el;
  }

  /**
   * Appends a message to both the in-memory/persisted history and the DOM.
   *
   * @param {ChatMessage} message
   */
  #appendMessage(message) {
    this.#history.push(message);
    this.#history = this.#history.slice(-MAX_STORED_MESSAGES);
    this.#saveHistory();
    this.#renderMessage(message);
    this.#scrollToBottom();
  }

  #renderHistory() {
    this.refs.messages.replaceChildren();
    if (this.#history.length === 0) {
      this.#renderMessage({
        role: 'assistant',
        content: "Hi! I can help you find a product, give advice, or answer questions about Sunday. What are you looking for?",
      });
      return;
    }
    for (const message of this.#history) {
      this.#renderMessage(message);
    }
    this.#scrollToBottom();
  }

  /**
   * @param {ChatMessage} message
   */
  #renderMessage(message) {
    if (message.content) {
      const bubble = document.createElement('div');
      bubble.className = `ai-chat__message ai-chat__message--${message.role}`;
      bubble.textContent = message.content;
      this.refs.messages.append(bubble);
    }

    if (message.products && message.products.length > 0) {
      this.refs.messages.append(this.#renderProductCards(message.products));
    }
  }

  /**
   * Renders search_products results as small clickable cards (image, title,
   * price, link) — kept separate from the assistant's text bubble so the
   * model never has to inline markdown links/images itself.
   *
   * @param {ProductResult[]} products
   * @returns {HTMLElement}
   */
  #renderProductCards(products) {
    const list = document.createElement('div');
    list.className = 'ai-chat__products';

    for (const product of products) {
      const card = document.createElement(product.url ? 'a' : 'div');
      card.className = 'ai-chat__product-card';
      if (product.url && card instanceof HTMLAnchorElement) {
        // Same-tab navigation (not target="_blank") — the widget's chat
        // history is persisted in sessionStorage, so the conversation is
        // still there if the user navigates back.
        card.href = product.url;
      }

      if (product.imageUrl) {
        const img = document.createElement('img');
        img.className = 'ai-chat__product-image';
        img.src = product.imageUrl;
        img.alt = product.title;
        img.loading = 'lazy';
        card.append(img);
      }

      const info = document.createElement('div');
      info.className = 'ai-chat__product-info';

      const title = document.createElement('span');
      title.className = 'ai-chat__product-title';
      title.textContent = product.title;
      info.append(title);

      if (product.price) {
        const price = document.createElement('span');
        price.className = 'ai-chat__product-price';
        price.textContent = product.currency ? `${product.price} ${product.currency}` : product.price;
        info.append(price);
      }

      if (!product.available) {
        const unavailable = document.createElement('span');
        unavailable.className = 'ai-chat__product-unavailable';
        unavailable.textContent = 'Out of stock';
        info.append(unavailable);
      }

      card.append(info);
      list.append(card);
    }

    return list;
  }

  #scrollToBottom() {
    const { messages } = this.refs;
    messages.scrollTop = messages.scrollHeight;
  }

  /**
   * @returns {ChatMessage[]}
   */
  #loadHistory() {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #saveHistory() {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(this.#history));
    } catch {
      // sessionStorage unavailable (private browsing, quota) — degrade to in-memory only.
    }
  }

  /**
   * @returns {boolean}
   */
  #loadOpenState() {
    try {
      return sessionStorage.getItem(OPEN_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * @param {boolean} isOpen
   */
  #saveOpenState(isOpen) {
    try {
      sessionStorage.setItem(OPEN_STORAGE_KEY, String(isOpen));
    } catch {
      // sessionStorage unavailable (private browsing, quota) — degrade to in-memory only:
      // the panel just won't stay open across a page navigation.
    }
  }
}

if (!customElements.get('ai-chat')) {
  customElements.define('ai-chat', AiChat);
}

export class AiChatErrorEvent extends CustomEvent {
  /**
   * @param {Error} error
   */
  constructor(error) {
    super(AiChatErrorEvent.eventName, { bubbles: true, detail: { error } });
  }

  static eventName = 'ai-chat:error';
}
